import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useSettings, useUpdateSettings } from '../api/hooks';
import { ENDPOINT_NAMES, type ForcedError } from '../api/types';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  PageHeader,
  ROW,
  Section,
  Select,
  Spinner,
  TABLE,
  TD,
  TH,
  THEAD_ROW,
} from '../components/ui';
import { useToast } from '../components/toast';

/** Codes documented in guide §3.3, exposed as one-click presets. */
const PRESETS: Record<number, { description: string; httpStatus: number }> = {
  2003: { description: 'Token introuvable', httpStatus: 401 },
  2009: { description: 'Token expiré', httpStatus: 401 },
  2051: { description: 'Lien déjà utilisé', httpStatus: 400 },
  2100: { description: 'Erreur technique', httpStatus: 500 },
};

export default function Erreurs() {
  const toast = useToast();
  const { data: settings, isPending, error } = useSettings();
  const update = useUpdateSettings();

  const [endpoint, setEndpoint] = useState<string>(ENDPOINT_NAMES[3]);
  const [code, setCode] = useState('2003');
  const [description, setDescription] = useState('Token introuvable');
  const [httpStatus, setHttpStatus] = useState('401');
  const [once, setOnce] = useState(true);

  const save = (forcedErrors: Record<string, ForcedError>, message: string) =>
    update.mutate(
      { forcedErrors },
      { onSuccess: () => toast.success(message), onError: (err) => toast.error(err) },
    );

  if (error) return <ErrorBox error={error} />;
  if (isPending || !settings) return <Spinner />;

  const rows = Object.entries(settings.forcedErrors);

  const add = () =>
    save(
      {
        ...settings.forcedErrors,
        [endpoint]: {
          code: Number(code),
          description,
          httpStatus: Number(httpStatus),
          once,
        },
      },
      `Erreur ${code} armée sur ${endpoint}`,
    );

  const applyPreset = (preset: number) => {
    const { description: presetDescription, httpStatus: presetStatus } = PRESETS[preset];
    setCode(String(preset));
    setDescription(presetDescription);
    setHttpStatus(String(presetStatus));
    save(
      {
        ...settings.forcedErrors,
        [endpoint]: { code: preset, description: presetDescription, httpStatus: presetStatus, once },
      },
      `Erreur ${preset} armée sur ${endpoint}`,
    );
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Erreurs forcées" />

      <Section title="Armer une erreur">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Endpoint">
            <Select value={endpoint} aria-label="Endpoint" onChange={(e) => setEndpoint(e.target.value)}>
              {ENDPOINT_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Code">
            <Input aria-label="Code" value={code} onChange={(e) => setCode(e.target.value)} className="w-24" />
          </Field>
          <Field label="Description">
            <Input aria-label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label="Statut HTTP">
            <Input
              aria-label="Statut HTTP"
              value={httpStatus}
              onChange={(e) => setHttpStatus(e.target.value)}
              className="w-24"
            />
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm text-on-surface-variant">
            <Checkbox checked={once} onChange={(e) => setOnce(e.target.checked)} />
            une seule fois
          </label>
          <Button size="sm" variant="primary" className="mb-1" disabled={update.isPending} onClick={add}>
            Ajouter
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-outline">Presets</span>
          {Object.keys(PRESETS).map((preset) => (
            <Button size="sm" key={preset} disabled={update.isPending} onClick={() => applyPreset(Number(preset))}>
              {preset}
            </Button>
          ))}
        </div>
      </Section>

      <Section title={`Erreurs armées (${rows.length})`}>
        {rows.length === 0 ? (
          <EmptyState>Aucune erreur forcée.</EmptyState>
        ) : (
          <table className={TABLE}>
            <thead>
              <tr className={THEAD_ROW}>
                <th className={TH}>Endpoint</th>
                <th className={TH}>Code</th>
                <th className={TH}>Description</th>
                <th className={TH}>HTTP</th>
                <th className={TH}>Une seule fois</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {rows.map(([name, forced]) => (
                <tr key={name} className={ROW}>
                  <td className={`${TD} font-semibold text-white`}>{name}</td>
                  <td className={TD}>{forced.code}</td>
                  <td className={TD}>{forced.description}</td>
                  <td className={TD}>{forced.httpStatus}</td>
                  <td className={TD}>{forced.once ? 'oui' : 'non'}</td>
                  <td className={TD}>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Retirer l’erreur de ${name}`}
                      disabled={update.isPending}
                      onClick={() => {
                        const next = { ...settings.forcedErrors };
                        delete next[name];
                        save(next, `Erreur retirée de ${name}`);
                      }}
                    >
                      <Trash2 size={15} aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
